// API Client - Base HTTP client for Blidz APIs
// This file provides the core HTTP client with proper typing and error handling
//
// ============================================================================
// Sentry Integration
// ============================================================================
// This client now automatically captures and sends the following data to Sentry
// for both successful and failed API requests (only for requests to our backend):
//
// REQUEST HEADERS:
// - authorization
// - x-csrftoken
// - device-id
// - x-blidz-client-version
//
// REQUEST COOKIES:
// - csrftoken
// - blidz_session_id
//
// RESPONSE HEADERS:
// - http-x-blidz-request-id
//
// Note: Request bodies are captured only when errors occur (for debugging).
// Response bodies are NOT captured for privacy/security reasons.
// For successful requests, the data is added as breadcrumbs and transaction context.
// For failed requests, the data is captured with exceptions for debugging.
// ============================================================================

import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";
import { devError, devLog } from "~/lib/dev-log";
import type { ApiResponse } from "./common";
import { CLIENT_CONFIG } from "~/config/client";

// ============================================================================
// Configuration
// ============================================================================

export interface ApiClientConfig {
  baseUrl: string;
  timeout?: number;
  defaultHeaders?: Record<string, string>;
  retryAttempts?: number;
  retryDelay?: number;
}

// ============================================================================
// Request Types
// ============================================================================

export interface RequestConfig {
  method?: "GET" | "POST";
  headers?: Record<string, string>;
  params?: Record<string, any>;
  timeout?: number;
  retries?: number;
  // Flag to indicate if this is a signup/login request (excludes CSRF token)
  isAuthRequest?: boolean;
}

// ============================================================================
// Sentry Integration Types
// ============================================================================

interface SentryApiContext {
  requestHeaders: Record<string, string>;
  requestCookies: Record<string, string>;
  responseHeaders: Record<string, string>;
  url: string;
  method: string;
  requestBody?: any;
  statusCode?: number;
}

// ============================================================================
// API Client Class
// ============================================================================

export class ApiClient {
  private config: Required<ApiClientConfig>;
  private deviceId?: string;
  private clientVersion?: string;

  // CSRF token update synchronization
  // Start with a promise to ensure first requests wait for auth to complete
  private csrfTokenUpdatePromise: Promise<void> | null = null;
  private csrfTokenUpdateResolve: ((value: void) => void) | null = null;

  constructor(config: ApiClientConfig) {
    this.config = {
      timeout: 30000,
      defaultHeaders: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      retryAttempts: 3,
      retryDelay: 1000,
      ...config,
    };

    // Initialize CSRF token update state to ensure first requests wait for auth to complete
    this.markCsrfTokenUpdateStarting();
  }

  // ============================================================================
  // Device Management
  // ============================================================================

  setDeviceInfo(deviceId: string, clientVersion: string) {
    this.deviceId = deviceId;
    this.clientVersion = clientVersion;
  }

  // ============================================================================
  // CSRF Token Update Synchronization
  // ============================================================================

  /**
   * Mark that a CSRF token update is starting
   * This prevents other requests from proceeding until the update is complete
   */
  private markCsrfTokenUpdateStarting(): void {
    if (this.csrfTokenUpdatePromise) {
      devLog("⚠️ CSRF token update already in progress");
      return;
    }

    this.csrfTokenUpdatePromise = new Promise<void>(resolve => {
      // Store the resolve function so we can call it when the update is complete
      this.csrfTokenUpdateResolve = resolve;
    });
    devLog("🔒 CSRF token update started - other requests will wait");
  }

  /**
   * Mark that a CSRF token update is complete
   * This allows other waiting requests to proceed
   */
  private markCsrfTokenUpdateComplete(): void {
    if (!this.csrfTokenUpdatePromise) {
      devLog("⚠️ No CSRF token update in progress to complete");
      return;
    }

    // Resolve the promise to allow waiting requests to proceed
    if (this.csrfTokenUpdateResolve) {
      this.csrfTokenUpdateResolve();
      this.csrfTokenUpdateResolve = null;
    }

    this.csrfTokenUpdatePromise = null;
    devLog("🔓 CSRF token update completed - other requests can proceed");
  }

  /**
   * Wait for any ongoing CSRF token update to complete
   * This ensures requests get the latest token after login/signup
   */
  private async waitForCsrfTokenUpdate(): Promise<void> {
    if (!this.csrfTokenUpdatePromise) {
      return;
    }

    devLog("⏳ Waiting for CSRF token update to complete...");
    await this.csrfTokenUpdatePromise;
    devLog("✅ CSRF token update wait complete");
  }

  /**
   * Check if a CSRF token update is currently in progress
   * This can be useful for debugging or monitoring
   */
  public isCsrfTokenUpdating(): boolean {
    return this.csrfTokenUpdatePromise !== null;
  }

  // ============================================================================
  // Cookie Utilities
  // ============================================================================

  /**
   * Get CSRF token from cookie
   * Since we now use the waiting mechanism, we just get the token directly
   */
  private getCsrfTokenFromCookie(): string | null {
    const csrfToken = Cookies.get("csrftoken");
    if (csrfToken) {
      devLog("✅ CSRF token found in cookie:", `${csrfToken.substring(0, 8)}...`);
    }
    return csrfToken || null;
  }

  // ============================================================================
  // Sentry Integration
  // ============================================================================

  /**
   * Capture specific request headers for Sentry
   */
  private captureRequestHeaders(requestHeaders: Record<string, string>): Record<string, string> {
    const capturedHeaders: Record<string, string> = {};

    // Capture specific headers we want to track
    // It's case-sensitive
    const headersToCapture = ["Authorization", "X-CSRFToken", "Device-Id", "x-blidz-client-version"];

    headersToCapture.forEach(headerName => {
      const value = requestHeaders[headerName];
      if (value) {
        capturedHeaders[headerName] = value;
      }
    });

    return capturedHeaders;
  }

  /**
   * Capture specific request cookies for Sentry
   */
  private captureRequestCookies(): Record<string, string> {
    const capturedCookies: Record<string, string> = {};

    // Capture specific cookies we want to track
    const cookiesToCapture = ["csrftoken", "blidz_session_id"];

    cookiesToCapture.forEach(cookieName => {
      const value = Cookies.get(cookieName);
      if (value) {
        capturedCookies[cookieName] = value;
      }
    });

    return capturedCookies;
  }

  /**
   * Capture specific response headers for Sentry
   */
  private captureResponseHeaders(response: Response): Record<string, string> {
    const capturedHeaders: Record<string, string> = {};

    // Capture specific headers we want to track
    const headersToCapture = ["http-x-blidz-request-id"];

    headersToCapture.forEach(headerName => {
      const value = response.headers.get(headerName);
      if (value) {
        capturedHeaders[headerName] = value;
      }
    });

    return capturedHeaders;
  }

  /**
   * Check if request is to our backend (to avoid capturing headers for external requests)
   */
  private isBackendRequest(url: string): boolean {
    try {
      const urlObj = new URL(url);
      const currentOrigin = window.location.origin;

      // Check if it's same origin (development with proxy)
      if (urlObj.origin === currentOrigin) {
        return true;
      }

      // Check if it's our backend domain (production/staging)
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      if (apiBaseUrl) {
        const apiUrlObj = new URL(apiBaseUrl);
        return urlObj.origin === apiUrlObj.origin;
      }

      return false;
    } catch {
      return false;
    }
  }

  /**
   * Send API context to Sentry
   */
  private sendToSentry(context: SentryApiContext, error: Error) {
    // Only capture for backend requests
    if (!this.isBackendRequest(context.url)) {
      return;
    }

    Sentry.withScope(scope => {
      // Add custom tags
      scope.setTag("api_error", true);

      // Add request context
      scope.setContext("api_request", {
        headers: context.requestHeaders,
        cookies: context.requestCookies,
        body: context.requestBody,
      });

      // Add response context
      scope.setContext("api_response", {
        headers: context.responseHeaders,
      });

      // Add extra data
      scope.setExtra("request_headers", context.requestHeaders);
      scope.setExtra("request_cookies", context.requestCookies);
      scope.setExtra("response_headers", context.responseHeaders);

      // Capture the error
      Sentry.captureException(error);
    });
  }

  /**
   * Send successful API context to Sentry as transaction context
   */
  private sendSuccessToSentry(context: SentryApiContext) {
    // Only capture for backend requests
    if (!this.isBackendRequest(context.url)) {
      return;
    }

    Sentry.withScope(scope => {
      // Add request context
      scope.setContext("api_request", {
        headers: context.requestHeaders,
        cookies: context.requestCookies,
        body: context.requestBody,
      });

      // Add response context
      scope.setContext("api_response", {
        headers: context.responseHeaders,
      });

      // Add extra data
      scope.setExtra("request_headers", context.requestHeaders);
      scope.setExtra("request_cookies", context.requestCookies);
      scope.setExtra("response_headers", context.responseHeaders);

      // Add breadcrumb for successful API call
      Sentry.addBreadcrumb({
        category: "api",
        level: "info",
        data: {
          headers: context.requestHeaders,
          cookies: context.requestCookies,
        },
      });
    });
  }

  // ============================================================================
  // Core Request Method
  // ============================================================================

  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const {
      method = "GET",
      headers = {},
      params = {},
      timeout = this.config.timeout,
      retries = this.config.retryAttempts,
      isAuthRequest = false,
    } = config;

    const url = this.buildUrl(endpoint);

    // Handle params based on HTTP method
    let requestBody: any = undefined;

    if (method === "GET") {
      // For GET requests, add params to URL query parameters
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    } else if (method === "POST") {
      // For POST requests, use params as the request body
      if (Object.keys(params).length > 0) {
        requestBody = params;
      }
    }

    // Build headers with common headers and per-request headers
    const requestHeaders = await this.buildHeaders(headers, isAuthRequest);

    // Build request options
    const requestOptions: RequestInit = {
      method,
      headers: requestHeaders,
      signal: AbortSignal.timeout(timeout),
      credentials: "include", // Include cookies in cross-origin requests
    };

    if (requestBody && method === "POST") {
      requestOptions.body = JSON.stringify(requestBody);
    }

    // Prepare Sentry context
    const sentryContext: SentryApiContext = {
      requestHeaders: this.captureRequestHeaders(requestHeaders),
      requestCookies: this.captureRequestCookies(),
      responseHeaders: {},
      url: url.toString(),
      method,
      requestBody,
    };

    // Execute request with retries
    let lastError: Error;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url.toString(), requestOptions);

        // Capture response headers for Sentry
        sentryContext.responseHeaders = this.captureResponseHeaders(response);
        sentryContext.statusCode = response.status;

        return await this.handleResponse<T>(response, sentryContext);
      } catch (error) {
        lastError = error as Error;

        // Update Sentry context with error information
        sentryContext.statusCode = (error as any).status || 0;

        // Only retry on network errors (including timeouts)
        const shouldRetry = this.isRetryableError(error as Error);

        if (attempt === retries || !shouldRetry) {
          // Send to Sentry on final attempt or non-retryable error
          this.sendToSentry(sentryContext, lastError);
          break;
        }

        // Wait before retry
        if (attempt < retries) {
          await this.delay(this.config.retryDelay * Math.pow(2, attempt));
        }
      }
    }

    throw lastError!;
  }

  // ============================================================================
  // Specialized Request Methods
  // ============================================================================

  /**
   * Handler for authentication-related API calls (signup/login)
   * These requests are marked as auth requests to exclude CSRF tokens
   * and signal when CSRF token updates are in progress
   */
  async makeAuthRequest<TResponse = any>(
    endpoint: string,
    params?: any,
    additionalHeaders?: Record<string, string>
  ): Promise<TResponse> {
    try {
      // Mark that a CSRF token update is starting
      // This will make other requests wait until this auth request completes
      this.markCsrfTokenUpdateStarting();

      const config: RequestConfig = {
        method: "POST",
        headers: additionalHeaders || {},
        params: params || {},
        isAuthRequest: true,
      };

      const response = await this.request<any>(endpoint, config);

      devLog(`✅ AUTH - Response received:`, response);

      // The backend returns a Blidz API response
      // Extract the content or return the response as-is
      const responseData = response.content || response;

      return responseData as TResponse;
    } catch (error) {
      devError(`❌ AUTH - Error:`, error);
      throw error;
    } finally {
      // Always mark the CSRF token update as complete, even if the request failed
      // This ensures other waiting requests don't get stuck
      this.markCsrfTokenUpdateComplete();
    }
  }

  /**
   * Handler for regular API calls that require authentication
   * These requests will include CSRF tokens and other session headers
   */
  async makeAuthenticatedRequest<TResponse = any>(
    endpoint: string,
    options: {
      method?: "GET" | "POST";
      params?: Record<string, any>;
      headers?: Record<string, string>;
    } = {}
  ): Promise<TResponse> {
    const { method = "POST", params = {}, headers = {} } = options;

    try {
      const config: RequestConfig = {
        method,
        headers,
        params,
        isAuthRequest: false,
      };

      const response = await this.request<any>(endpoint, config);

      devLog(`✅ AUTHENTICATED - Response received:`, response);

      // The backend returns a Blidz API response
      // Extract the content or return the response as-is
      const responseData = response.content || response;

      return responseData as TResponse;
    } catch (error) {
      devError(`❌ AUTHENTICATED - Error:`, error);
      throw error;
    }
  }

  /**
   * Handler for file upload requests using modern Fetch API
   * These requests use FormData and require authentication
   */
  async makeFileUploadRequest<TResponse = any>(
    endpoint: string,
    files: File[],
    additionalData?: Record<string, string>,
    options?: {
      headers?: Record<string, string>;
      timeout?: number;
      onProgress?: (progress: number) => void;
    }
  ): Promise<TResponse> {
    const { headers = {}, timeout = this.config.timeout } = options || {};

    try {
      const url = this.buildUrl(endpoint);

      // Create FormData
      const formData = new FormData();

      // Add files to FormData
      files.forEach(file => {
        formData.append("files", file);
      });

      // Add additional data if provided
      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }

      // Build headers (exclude Content-Type to let browser set it with boundary)
      const requestHeaders = await this.buildHeaders(headers, false);
      delete requestHeaders["Content-Type"]; // Let browser set multipart/form-data boundary

      // Prepare Sentry context
      const sentryContext: SentryApiContext = {
        requestHeaders: this.captureRequestHeaders(requestHeaders),
        requestCookies: this.captureRequestCookies(),
        responseHeaders: {},
        url: url.toString(),
        method: "POST",
        requestBody: { files: files.map(f => f.name), ...additionalData },
      };

      // Create AbortController for timeout handling
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => {
        abortController.abort();
      }, timeout);

      try {
        // Use modern fetch API
        const response = await fetch(url.toString(), {
          method: "POST",
          headers: requestHeaders,
          body: formData,
          signal: abortController.signal,
        });

        clearTimeout(timeoutId);

        // Capture response headers for Sentry
        sentryContext.responseHeaders = this.captureResponseHeaders(response);
        sentryContext.statusCode = response.status;

        if (!response.ok) {
          let errorData: any;
          try {
            errorData = await response.json();
          } catch {
            errorData = { message: (await response.text()) || "Upload failed" };
          }

          const error_code = errorData.code || "upload_error";
          const error_message = errorData.message || "Upload failed";
          const error_user_message = errorData.userMessage || "Failed to upload files. Please try again.";
          const clientError = new ApiClientError(response.status, error_code, error_message, error_user_message);
          this.sendToSentry(sentryContext, clientError);
          throw clientError;
        }

        // Parse successful response
        let responseData: any;
        try {
          responseData = await response.json();
        } catch (_parseError) {
          const error = new ApiClientError(response.status, "PARSE_ERROR", "Failed to parse response");
          this.sendToSentry(sentryContext, error);
          throw error;
        }

        // Send successful context to Sentry
        this.sendSuccessToSentry(sentryContext);

        devLog(`✅ FILE UPLOAD - Response received:`, responseData);

        // The backend returns a Blidz API response
        // Extract the content or return the response as-is
        const responseContent = responseData.content || responseData;
        return responseContent as TResponse;
      } catch (error) {
        clearTimeout(timeoutId);

        if (error instanceof Error) {
          if (error.name === "AbortError") {
            const timeoutError = new ApiClientError(408, "TIMEOUT_ERROR", "Upload timeout");
            this.sendToSentry(sentryContext, timeoutError);
            throw timeoutError;
          }

          if (error.message.includes("fetch") || error.message.includes("network")) {
            const networkError = new ApiClientError(0, "NETWORK_ERROR", "Network error during upload");
            this.sendToSentry(sentryContext, networkError);
            throw networkError;
          }
        }

        this.sendToSentry(sentryContext, error as Error);
        throw error;
      }
    } catch (error) {
      devError(`❌ FILE UPLOAD - Error:`, error);
      throw error;
    }
  }

  // ============================================================================
  // Header Building Logic
  // ============================================================================

  private async buildHeaders(
    requestHeaders: Record<string, string> = {},
    isAuthRequest = false
  ): Promise<Record<string, string>> {
    const headers = {
      ...this.config.defaultHeaders,
    };

    // Always add x-blidz-client-version for every request
    if (this.clientVersion) {
      headers["x-blidz-client-version"] = this.clientVersion;
    }

    // Add CSRF token for requests other than signup/login
    if (!isAuthRequest) {
      // Wait for any ongoing CSRF token updates to complete
      // This ensures we get the latest token after login/signup
      await this.waitForCsrfTokenUpdate();

      const csrfToken = this.getCsrfTokenFromCookie();
      if (csrfToken) {
        headers["X-CSRFToken"] = csrfToken;
      } else {
        devLog("ℹ️ No CSRF token in headers - relying on automatic cookie transmission");
      }
    }

    if (this.deviceId) {
      headers["Device-Id"] = this.deviceId;
    }

    // Merge with per-request headers (per-request headers take precedence)
    return {
      ...headers,
      ...requestHeaders,
    };
  }

  // ============================================================================
  // Response Handling
  // ============================================================================

  private async handleResponse<T>(response: Response, context: SentryApiContext): Promise<ApiResponse<T>> {
    const contentType = response.headers.get("content-type");
    const isJson = contentType?.includes("application/json");

    let responseData: any;
    try {
      responseData = isJson ? await response.json() : await response.text();
    } catch (_parseError) {
      // Update context with response data for Sentry
      context.statusCode = response.status;

      const error = new ApiClientError(response.status, "PARSE_ERROR", "Failed to parse response");
      this.sendToSentry(context, error);
      throw error;
    }

    // Update context with response data for Sentry
    context.statusCode = response.status;

    if (!response.ok) {
      const error_code = isJson ? responseData.code : "api_error";
      const error_message = (isJson ? responseData.message : responseData) || "API Error";
      const error_user_message = isJson ? responseData.userMessage : "Failed. Please try again later";
      const clientError = new ApiClientError(response.status, error_code, error_message, error_user_message);
      this.sendToSentry(context, clientError);
      throw clientError;
    }

    // Send successful context to Sentry
    this.sendSuccessToSentry(context);

    return responseData as ApiResponse<T>;
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Determine if an error should trigger a retry
   * Only network errors and timeouts should be retried
   */
  private isRetryableError(error: Error): boolean {
    // Timeout errors
    if (error.name === "TimeoutError" || error.name === "AbortError") {
      return true;
    }

    // API client network errors
    if (error instanceof ApiClientError && error.isNetworkError) {
      return true;
    }

    // Don't retry HTTP errors (4xx, 5xx), parsing errors, etc.
    return false;
  }

  // ============================================================================
  // URL Building Helpers
  // ============================================================================

  buildUrl(endpoint: string): URL {
    // Ensure baseUrl ends with a slash
    let baseUrl = this.config.baseUrl;
    if (!baseUrl) {
      baseUrl = window.location.origin;
    }

    baseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;

    // Ensure endpoint starts without a slash and prepend 'api/'
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
    const apiEndpoint = `api/${cleanEndpoint}`;

    const url = new URL(apiEndpoint, baseUrl);
    devLog("🌐 URL:", url.toString());
    return url;
  }
}

// ============================================================================
// Custom Error Class
// ============================================================================

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    public message: string,
    public userMessage?: string
  ) {
    // Construct a meaningful error message for Sentry
    const errorMessage = ApiClientError.buildErrorMessage(status, code, message, userMessage);
    super(errorMessage);
    this.name = "ApiClientError";
  }

  /**
   * Build a comprehensive error message that includes code and status
   * This ensures Sentry always shows meaningful information
   */
  private static buildErrorMessage(status: number, code: string, message: string, userMessage?: string): string {
    const parts: string[] = [];

    // Add status code for additional context
    if (status) {
      parts.push(`HTTP ${status}`);
    }

    // Add the error code if available
    if (code) {
      parts.push(code);
    }

    // Add the message if it's not empty
    if (message && message.trim()) {
      parts.push(message);
    }

    // Add the user message if it's not empty
    if (userMessage && userMessage.trim()) {
      parts.push(userMessage);
    }

    return parts.join(" - ");
  }

  get isNetworkError(): boolean {
    if (
      this.status === 0 ||
      this.status === 408 ||
      this.status === 504 ||
      this.code === "NETWORK_ERROR" ||
      this.code === "TIMEOUT_ERROR"
    ) {
      return true;
    }

    return false;
  }
}

// ============================================================================
// Default Client Instance
// ============================================================================

// Generate a unique device ID for this browser session
const generateDeviceId = (): string => {
  // Check if we're in a browser environment
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    // Server-side or environment without localStorage
    const randomPart = Math.random().toString(36).substr(2, 9);
    const timePart = Date.now().toString(36);
    return `web-ssr-${randomPart}-${timePart}`;
  }

  // Try to get from localStorage first
  let deviceId = localStorage.getItem("blidz-device-id");
  if (!deviceId) {
    // Generate a new UUID-like device ID
    const randomPart = Math.random().toString(36).substr(2, 9);
    const timePart = Date.now().toString(36);
    deviceId = `web-${randomPart}-${timePart}`;
    localStorage.setItem("blidz-device-id", deviceId);
  }
  return deviceId;
};

// Configuration for different environments
// Note: Using Vite proxy for development to avoid CORS issues
// Set VITE_API_BASE_URL environment variable to override

// Get the API base URL
const getApiBaseUrl = (): string => {
  // Check environment variable first
  if (import.meta.env.VITE_API_BASE_URL) {
    devLog("Using API URL from environment:", import.meta.env.VITE_API_BASE_URL);
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Use relative URL for development (works with Vite proxy)
  // This avoids CORS issues by using the same origin
  const defaultUrl = "";
  devLog("Using relative API URL (via Vite proxy)");
  return defaultUrl;
};

// Create default client instance (can be configured later)
export const apiClient = new ApiClient({
  baseUrl: getApiBaseUrl(),
});

// Set required headers for Blidz backend
apiClient.setDeviceInfo(
  generateDeviceId(),
  CLIENT_CONFIG.VERSION // Client version
);
